/**
 * The silhouette — spec v6b §2, `docs/adr/9013`.
 *
 * A screen whose read has not answered with data yet is drawn in its FINAL shape: the same
 * components, in the same place, at the same height, with `—` where a figure will be and a
 * grey block where a bar will be. When the data lands nothing moves; the grey fills in.
 *
 * The ghost is a MODE of the component that will hold the data (`<KpiBand ghost>`,
 * `<DataTable ghost>`, `<Chart ghost>`), never a separate empty-state component beside it —
 * that is what keeps the layout still. It reaches the figures inside a band through this
 * context, so a screen writes its `Measure`s once and flips one prop.
 *
 * **Ghost is not zero** (leader ruling 5, reqs/00 D9). A ghosted figure renders `—` WHATEVER
 * value it was handed — a count computed over an empty list is `0`, and `0 transfers` states a
 * finding ("the engine proposed none") that nobody has made yet. The strip above the screen
 * says why there is nothing; the silhouette states nothing at all.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

const GhostContext = createContext(false);

export interface GhostProviderProps {
  /** Default `true`. `false` inside a ghosted region re-enables the figures beneath it. */
  readonly ghost?: boolean;
  readonly children: ReactNode;
}

/** Puts every `Measure`, `NumericCell`, bar cell and `ProportionBar` beneath it in ghost mode. */
export function GhostProvider({ ghost = true, children }: GhostProviderProps): React.JSX.Element {
  return <GhostContext.Provider value={ghost}>{children}</GhostContext.Provider>;
}

/** Whether the figures here are a silhouette. */
export function useGhost(): boolean {
  return useContext(GhostContext);
}

/** What a ghosted figure shows. The same glyph as an absence, in every locale — not copy. */
export const GHOST_MARK = '—';

export interface GhostBarProps {
  /** How much of the cell the grey block spans — a fixed shape, never a value. */
  readonly width?: string;
  /** `end` for a numeric column, so the silhouette keeps the column's alignment. */
  readonly align?: 'start' | 'end';
}

/**
 * One grey block, 12px — the unit of a ghost row (spec v6b §2: "5 righe grigie da 12px").
 * Decorative: the region around it carries the words.
 */
export function GhostBar({ width = '70%', align = 'start' }: GhostBarProps): React.JSX.Element {
  return (
    <span
      aria-hidden
      data-ghost-bar=""
      className="bg-ghost block h-3 rounded-chip"
      style={{ inlineSize: width, marginInlineStart: align === 'end' ? 'auto' : undefined }}
    />
  );
}
